import { resourceRef, shortId, type ListFindingsQuery } from '@hlt/contracts';

/** Findings list state kept in the URL, so filters survive back navigation and can be shared. */
export interface FindingListSearch {
    /** Default: open findings, the follow-up queue. */
    status?: 'fixed' | 'all';
    /** `me` or a person's id. */
    owner?: string;
    area?: string;
    q?: string;
    cursor?: string;
}

/**
 * Person and area in the address are written in the 22-character form of their UUID to keep addresses
 * short; UUIDs from older addresses are read as well. The page receives UUIDs.
 */
export function parseFindingSearch(search: Record<string, unknown>): FindingListSearch {
    const text = (value: unknown, max: number) =>
        typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined;
    const ownerText = text(search.owner, 36);
    const owner = ownerText === 'me' ? 'me' : ownerText ? resourceRef(ownerText) : null;
    const areaText = text(search.area, 36);
    const area = areaText ? resourceRef(areaText) : null;
    return {
        ...(search.status === 'fixed' || search.status === 'all' ? { status: search.status } : {}),
        ...(owner ? { owner } : {}),
        ...(area ? { area } : {}),
        ...(text(search.q, 200) ? { q: text(search.q, 200) } : {}),
        ...(text(search.cursor, 1024) ? { cursor: text(search.cursor, 1024) } : {}),
    };
}

export function toFindingQuery(search: FindingListSearch, userId: string | null): ListFindingsQuery {
    const ownerId = search.owner === 'me' ? (userId ?? undefined) : search.owner;
    return {
        limit: 25,
        ...(search.status === 'all' ? {} : { status: search.status ?? 'open' }),
        ...(ownerId ? { ownerId } : {}),
        ...(search.area ? { areaId: search.area } : {}),
        ...(search.q ? { search: search.q } : {}),
        ...(search.cursor ? { cursor: search.cursor } : {}),
    };
}

/** The search as the address shows it: person and area in their short form. */
export function toAddressSearch(search: FindingListSearch): FindingListSearch {
    return {
        ...search,
        ...(search.owner && search.owner !== 'me' ? { owner: shortId(search.owner) } : {}),
        ...(search.area ? { area: shortId(search.area) } : {}),
    };
}

/** Filters other than the default status, for the clear action and the empty state. */
export const isFiltered = (search: FindingListSearch): boolean =>
    Boolean(search.status || search.owner || search.area || search.q);
